import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useUser } from '../context/UserContext';
import { getGraficosSegCom, getFactoringResumen, getFactoringSeguimientoEjecutivo, getUsuarios } from '../api/apiConfig';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatNumberEsCL } from '../utils/numberFormat';
import { canUsePermissionDebug, userFromListRow } from '../utils/permissionDebug';

function toIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultDateRange() {
  const hasta = new Date();
  const desde = new Date();
  desde.setDate(desde.getDate() - 20);
  return { desde: toIsoDate(desde), hasta: toIsoDate(hasta) };
}

const Factoring = () => {
  const navigate = useNavigate();
  const { user: userData, realUser } = useUser();
  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);
  const [error, setError] = useState(null);

  const defaults = useMemo(() => defaultDateRange(), []);
  const sesionPrivilegiada = canUsePermissionDebug(realUser || userData);
  const [fechaDesde, setFechaDesde] = useState(defaults.desde);
  const [fechaHasta, setFechaHasta] = useState(defaults.hasta);
  const [vistaModo, setVistaModo] = useState('total'); // total | usuario
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioComoEmail, setUsuarioComoEmail] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (!sesionPrivilegiada) return;
    let cancelled = false;
    (async () => {
      setLoadingUsers(true);
      try {
        const usersRes = await getUsuarios().catch(() => []);
        if (cancelled) return;
        const usersArr = Array.isArray(usersRes) ? usersRes : usersRes?.data || [];
        const mapped = usersArr.map(userFromListRow).filter(Boolean);
        mapped.sort((a, b) =>
          String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es', {
            sensitivity: 'base',
          }),
        );
        setUsuarios(mapped);
      } catch (e) {
        console.error('Error cargando usuarios:', e);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sesionPrivilegiada]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return usuarios;
    return usuarios.filter((u) => {
      const hay = `${u.nombre} ${u.email} ${u.tipo_usuario} ${u.grupo}`.toLowerCase();
      return hay.includes(q);
    });
  }, [usuarios, userSearch]);

  const selectedComo = useMemo(
    () => usuarios.find((u) => u.email === usuarioComoEmail) || null,
    [usuarios, usuarioComoEmail],
  );

  const buildResumenOptions = useCallback(() => {
    if (!sesionPrivilegiada) return {};
    const options = {
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta,
    };
    if (vistaModo === 'usuario' && selectedComo) {
      options.ver_como = {
        mail: selectedComo.email,
        email: selectedComo.email,
        tipo_usuario: selectedComo.tipo_usuario,
        grupo: selectedComo.grupo || '',
      };
    }
    return options;
  }, [sesionPrivilegiada, fechaDesde, fechaHasta, vistaModo, selectedComo]);

  const fetchChartData = useCallback(async () => {
    if (!userData) return;
    try {
      setLoading(true);
      const response = await getGraficosSegCom(userData);

      let data = response;
      if (response && response.statusCode === 200 && response.body) {
        data = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
      }

      if (Array.isArray(data)) {
        const formattedData = data
          .map((item) => {
            const [year, month, day] = item.FECHA_REPOR.split('-');
            return {
              ...item,
              FECHA_REPOR: `${day}-${month}-${year}`,
            };
          })
          .sort(
            (a, b) =>
              new Date(a.FECHA_REPOR.split('-').reverse().join('-')) -
              new Date(b.FECHA_REPOR.split('-').reverse().join('-')),
          );

        setChartData(formattedData);
      }
    } catch (err) {
      setError('Error al cargar los datos de gráficos');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [userData]);

  const fetchTableData = useCallback(async () => {
    const caller = sesionPrivilegiada ? realUser || userData : userData;
    if (!caller) return;
    if (sesionPrivilegiada && vistaModo === 'usuario' && !selectedComo) {
      setTableData([]);
      return;
    }
    try {
      setLoadingTable(true);
      const response = await getFactoringResumen(caller, buildResumenOptions());

      let data = response;
      if (response && response.statusCode === 200 && response.body) {
        data = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
      }

      if (Array.isArray(data)) {
        const formattedData = data
          .map((item) => {
            const [year, month, day] = item.FECHA_REPOR.split('-');
            return {
              ...item,
              FECHA_REPOR: `${day}-${month}-${year}`,
            };
          })
          .sort(
            (a, b) =>
              new Date(b.FECHA_REPOR.split('-').reverse().join('-')) -
              new Date(a.FECHA_REPOR.split('-').reverse().join('-')),
          );

        setTableData(formattedData);
      } else {
        setTableData([]);
      }
    } catch (err) {
      console.error('Error al cargar datos de tabla:', err);
      setTableData([]);
    } finally {
      setLoadingTable(false);
    }
  }, [
    userData,
    realUser,
    sesionPrivilegiada,
    vistaModo,
    selectedComo,
    buildResumenOptions,
  ]);

  useEffect(() => {
    if (userData) {
      fetchChartData();
    }
  }, [userData, fetchChartData]);

  useEffect(() => {
    if (userData || realUser) {
      fetchTableData();
    }
  }, [userData, realUser, fetchTableData]);

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const getComplianceColor = (cumplimiento, debeSer) => {
    if (cumplimiento >= debeSer) {
      return '#dcfce7';
    } else if (cumplimiento < debeSer * 0.7) {
      return '#fecaca';
    } else {
      return '#fef3c7';
    }
  };

  const formatCurrency = (value) => {
    return `${formatNumberEsCL(value, { minimumFractionDigits: 0, maximumFractionDigits: 1 })} MM$`;
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('es-CL').format(value);
  };

  const formatTooltipCurrency = (value) => {
    const n = Number(value);
    const decimals = Number.isFinite(n) && Math.abs(n - Math.round(n)) < 1e-9 ? 0 : 2;
    return `${formatNumberEsCL(value, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} MM$`;
  };

  const handleDetalleSeguimiento = async (fecha) => {
    try {
      const [day, month, year] = fecha.split('-');
      const fechaFormateada = `${year}${month}${day}`;

      const response = await getFactoringSeguimientoEjecutivo(userData, fechaFormateada);

      let data = response;
      if (response && response.statusCode === 200 && response.body) {
        data = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
      }

      navigate('/detalle-seguimiento-ejecutivo', { state: { data, fecha } });
    } catch (error) {
      console.error('Error al obtener detalle de seguimiento:', error);
    }
  };

  const vistaLabel =
    vistaModo === 'usuario' && selectedComo
      ? `Vista de ${selectedComo.nombre} (${selectedComo.tipo_usuario})`
      : 'Total (tu resumen)';

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Factoring</h1>
      </div>

      <div className="flex gap-4 mb-6">
        <button onClick={() => navigate('/detalle-otorgamientos')} className="btn-primary">
          Detalle de otorgamientos
        </button>

        <button onClick={() => navigate('/detalle-recaudaciones')} className="btn-primary">
          Detalle de recaudaciones
        </button>

        <button
          onClick={() => navigate('/detalle-devoluciones-manuales')}
          className="btn-primary"
        >
          Detalle Devoluciones de intereses manuales
        </button>
      </div>

      {sesionPrivilegiada && (
        <div
          className="mb-6 p-4"
          style={{
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
          }}
        >
          <div
            style={{
              color: 'var(--if-primary)',
              fontWeight: 600,
              fontSize: '0.95rem',
              marginBottom: '12px',
            }}
          >
            Filtros del resumen
          </div>
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Fecha desde</label>
              <input
                type="date"
                value={fechaDesde}
                max={fechaHasta}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Fecha hasta</label>
              <input
                type="date"
                value={fechaHasta}
                min={fechaDesde}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Ver</label>
              <select
                value={vistaModo}
                onChange={(e) => setVistaModo(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm min-w-[180px]"
              >
                <option value="total">Total (mi resumen)</option>
                <option value="usuario">Como otro usuario</option>
              </select>
            </div>
            {vistaModo === 'usuario' && (
              <>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Buscar usuario</label>
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Nombre o correo…"
                    className="border border-gray-300 rounded px-2 py-1.5 text-sm min-w-[180px]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Usuario</label>
                  <select
                    value={usuarioComoEmail}
                    onChange={(e) => setUsuarioComoEmail(e.target.value)}
                    disabled={loadingUsers}
                    className="border border-gray-300 rounded px-2 py-1.5 text-sm min-w-[260px] max-w-[360px]"
                  >
                    <option value="">
                      {loadingUsers ? 'Cargando…' : 'Selecciona un usuario'}
                    </option>
                    {filteredUsers.map((u) => (
                      <option key={u.email} value={u.email}>
                        {u.nombre} — {u.tipo_usuario}
                        {u.grupo ? ` / ${u.grupo}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <button
              type="button"
              onClick={fetchTableData}
              className="btn-primary"
              disabled={loadingTable || (vistaModo === 'usuario' && !selectedComo)}
            >
              {loadingTable ? 'Actualizando…' : 'Aplicar'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">{vistaLabel}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Cargando datos...</div>
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-red-600">{error}</div>
        </div>
      ) : (
        <>
          {loadingTable && (
            <div className="mb-3 text-sm text-gray-500">Actualizando resumen…</div>
          )}

          {tableData.length > 0 && (
            <div className="seccion-datos mb-6">
              <h3
                style={{
                  color: 'var(--if-primary)',
                  margin: '0 0 1rem 0',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                }}
              >
                Resumen de Seguimiento Comercial
              </h3>

              <div
                className="mb-4 p-3"
                style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                }}
              >
                <div
                  style={{
                    color: 'var(--if-primary)',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    marginBottom: '8px',
                  }}
                >
                  Metas de Cumplimiento por Fecha:
                </div>
                <div className="flex flex-wrap gap-4 mb-2">
                  {tableData.map((item, index) => (
                    <div key={index} className="text-center">
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '2px' }}>
                        {item.FECHA_REPOR}
                      </div>
                      <div className="flex items-center justify-center gap-1">
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: '#10b981',
                            borderRadius: '50%',
                          }}
                        ></div>
                        <span style={{ fontSize: '0.8rem', fontWeight: '500', color: '#374151' }}>
                          {formatPercentage(item.CUMPLIMIENTO_DEBE_SER)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-xs" style={{ color: '#9ca3af' }}>
                  <span style={{ color: '#ef4444' }}>●</span> &lt;70% |
                  <span style={{ color: '#f59e0b' }}>●</span> 70%-Meta |
                  <span style={{ color: '#10b981' }}>●</span> ≥Meta
                </div>
              </div>
              <div className="tabla-container" style={{ overflowX: 'auto' }}>
                <table
                  className="w-full"
                  style={{
                    fontSize: '0.875rem',
                    minWidth: userData?.tipo_usuario !== 'Ejecutivo' ? '1420px' : '1320px',
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          width: '120px',
                          minWidth: '120px',
                          position: 'sticky',
                          left: 0,
                          backgroundColor: 'var(--if-primary)',
                          zIndex: 10,
                        }}
                      >
                        Fecha
                      </th>
                      <th
                        style={{
                          backgroundColor: '#fef3c7',
                          color: 'white',
                          minWidth: '130px',
                        }}
                      >
                        Margen Negocio
                      </th>
                      <th
                        style={{
                          backgroundColor: '#fef3c7',
                          color: 'white',
                          minWidth: '100px',
                        }}
                      >
                        Mora
                      </th>
                      <th
                        style={{
                          backgroundColor: '#fef3c7',
                          color: 'white',
                          minWidth: '120px',
                        }}
                      >
                        Meta Margen
                      </th>
                      <th
                        style={{
                          backgroundColor: '#fef3c7',
                          color: 'white',
                          minWidth: '120px',
                        }}
                      >
                        Margen Total
                      </th>
                      <th
                        style={{
                          backgroundColor: '#fef3c7',
                          color: 'white',
                          minWidth: '110px',
                        }}
                      >
                        Cumpl. Margen
                      </th>
                      <th
                        style={{
                          backgroundColor: '#dbeafe',
                          color: 'white',
                          minWidth: '120px',
                        }}
                      >
                        Meta Volumen
                      </th>
                      <th
                        style={{
                          backgroundColor: '#dbeafe',
                          color: 'white',
                          minWidth: '100px',
                        }}
                      >
                        Volumen
                      </th>
                      <th
                        style={{
                          backgroundColor: '#dbeafe',
                          color: 'white',
                          minWidth: '110px',
                        }}
                      >
                        Cumpl. Volumen
                      </th>
                      <th
                        style={{
                          backgroundColor: '#dcfce7',
                          color: 'white',
                          minWidth: '110px',
                        }}
                      >
                        Meta Clientes
                      </th>
                      <th
                        style={{
                          backgroundColor: '#dcfce7',
                          color: 'white',
                          minWidth: '100px',
                        }}
                      >
                        Clientes Op.
                      </th>
                      <th
                        style={{
                          backgroundColor: '#dcfce7',
                          color: 'white',
                          minWidth: '110px',
                        }}
                      >
                        Cumpl. Clientes
                      </th>
                      <th
                        style={{
                          backgroundColor: '#f3e8ff',
                          color: 'white',
                          minWidth: '100px',
                        }}
                      >
                        Cumpl. Final
                      </th>
                      {userData?.tipo_usuario !== 'Ejecutivo' && (
                        <th
                          style={{
                            backgroundColor: '#fef3c7',
                            color: 'white',
                            minWidth: '100px',
                          }}
                        >
                          Detalle
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((item, index) => (
                      <tr key={index}>
                        <td
                          style={{
                            whiteSpace: 'nowrap',
                            position: 'sticky',
                            left: 0,
                            backgroundColor: 'white',
                            zIndex: 9,
                            borderRight: '1px solid #e2e8f0',
                          }}
                        >
                          {item.FECHA_REPOR}
                        </td>
                        <td style={{ textAlign: 'right', backgroundColor: '#fffbeb' }}>
                          MM${' '}
                          {new Intl.NumberFormat('es-CL', {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          }).format(item.MARGEN_NEGOCIO)}
                        </td>
                        <td style={{ textAlign: 'right', backgroundColor: '#fffbeb' }}>
                          MM${' '}
                          {new Intl.NumberFormat('es-CL', {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          }).format(item.MORA)}
                        </td>
                        <td style={{ textAlign: 'right', backgroundColor: '#fffbeb' }}>
                          MM${' '}
                          {new Intl.NumberFormat('es-CL', {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          }).format(item.META_MARGEN)}
                        </td>
                        <td style={{ textAlign: 'right', backgroundColor: '#fffbeb' }}>
                          MM${' '}
                          {new Intl.NumberFormat('es-CL', {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          }).format(item.MARGEN_TOTAL)}
                        </td>
                        <td
                          style={{
                            textAlign: 'right',
                            backgroundColor: getComplianceColor(
                              item.CUMPLIMIENTO_MARGEN,
                              item.CUMPLIMIENTO_DEBE_SER,
                            ),
                          }}
                        >
                          {formatPercentage(item.CUMPLIMIENTO_MARGEN)}
                        </td>
                        <td style={{ textAlign: 'right', backgroundColor: '#eff6ff' }}>
                          MM${' '}
                          {new Intl.NumberFormat('es-CL', {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          }).format(item.META_VOLUMEN)}
                        </td>
                        <td style={{ textAlign: 'right', backgroundColor: '#eff6ff' }}>
                          MM${' '}
                          {new Intl.NumberFormat('es-CL', {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          }).format(item.VOLUMEN)}
                        </td>
                        <td
                          style={{
                            textAlign: 'right',
                            backgroundColor: getComplianceColor(
                              item.CUMPLIMIENTO_VOLUMEN,
                              item.CUMPLIMIENTO_DEBE_SER,
                            ),
                          }}
                        >
                          {formatPercentage(item.CUMPLIMIENTO_VOLUMEN)}
                        </td>
                        <td style={{ textAlign: 'right', backgroundColor: '#f0fdf4' }}>
                          {new Intl.NumberFormat('es-CL').format(item.META_CLIENTES_OPERANDO)}
                        </td>
                        <td style={{ textAlign: 'right', backgroundColor: '#f0fdf4' }}>
                          {item.CLIENTES_OPERANDO}
                        </td>
                        <td
                          style={{
                            textAlign: 'right',
                            backgroundColor: getComplianceColor(
                              item.CUMPLIMIENTO_CLIENTES_OPERANDO,
                              item.CUMPLIMIENTO_DEBE_SER,
                            ),
                          }}
                        >
                          {formatPercentage(item.CUMPLIMIENTO_CLIENTES_OPERANDO)}
                        </td>
                        <td
                          style={{
                            textAlign: 'right',
                            backgroundColor: getComplianceColor(
                              item.CUMPLIMIENTO_FINAL,
                              item.CUMPLIMIENTO_DEBE_SER,
                            ),
                          }}
                        >
                          {formatPercentage(item.CUMPLIMIENTO_FINAL)}
                        </td>
                        {userData?.tipo_usuario !== 'Ejecutivo' && (
                          <td style={{ textAlign: 'center', backgroundColor: '#fffbeb' }}>
                            <button
                              onClick={() => handleDetalleSeguimiento(item.FECHA_REPOR)}
                              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            >
                              Ver Detalle
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!loadingTable && tableData.length === 0 && (
            <div className="mb-6 text-sm text-gray-500">
              {sesionPrivilegiada && vistaModo === 'usuario' && !selectedComo
                ? 'Selecciona un usuario para ver su resumen.'
                : 'Sin datos de resumen para el rango seleccionado.'}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="seccion-datos">
              <h3
                style={{
                  color: 'var(--if-primary)',
                  margin: '0 0 1rem 0',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                }}
              >
                Monto Anticipado
              </h3>
              <div style={{ height: '250px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="FECHA_REPOR"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={formatCurrency} />
                    <Tooltip
                      formatter={(value) => [formatTooltipCurrency(value), 'Monto Anterior']}
                    />
                    <Line
                      type="linear"
                      dataKey="MON_ANT"
                      stroke="var(--if-primary)"
                      strokeWidth={2}
                      dot={{ fill: 'var(--if-primary)', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="seccion-datos">
              <h3
                style={{
                  color: 'var(--if-primary)',
                  margin: '0 0 1rem 0',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                }}
              >
                Número de Clientes
              </h3>
              <div style={{ height: '250px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="FECHA_REPOR"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={formatNumber} />
                    <Tooltip formatter={(value) => [formatNumber(value), 'Número de Clientes']} />
                    <Line
                      type="linear"
                      dataKey="NUM_CLIENTE"
                      stroke="#16a34a"
                      strokeWidth={2}
                      dot={{ fill: '#16a34a', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="seccion-datos">
              <h3
                style={{
                  color: 'var(--if-primary)',
                  margin: '0 0 1rem 0',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                }}
              >
                Margen de Negocio
              </h3>
              <div style={{ height: '250px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="FECHA_REPOR"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={formatCurrency} />
                    <Tooltip
                      formatter={(value) => [formatTooltipCurrency(value), 'Margen de Negocio']}
                    />
                    <Line
                      type="linear"
                      dataKey="MARGEN_NEGOCIO"
                      stroke="#dc2626"
                      strokeWidth={2}
                      dot={{ fill: '#dc2626', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="seccion-datos">
              <h3
                style={{
                  color: 'var(--if-primary)',
                  margin: '0 0 1rem 0',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                }}
              >
                Mora
              </h3>
              <div style={{ height: '250px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="FECHA_REPOR"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={formatCurrency} />
                    <Tooltip formatter={(value) => [formatTooltipCurrency(value), 'Mora']} />
                    <Line
                      type="linear"
                      dataKey="MORA"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Factoring;
